import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, Field, Icon, Input, Modal, Select, Spinner, StatusPill, Textarea, useToast } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl, datePt, dateTimePt, statusLabel, todayIso } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface ApptRow {
  id: string;
  code: string;
  business_id: string;
  professional_id: string;
  service_id: string;
  customer_id: string;
  service_name: string;
  service_duration_minutes: number;
  price_cents: number;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  status: string;
  notes: string | null;
  cancellation_reason: string | null;
  hold_expires_at: string | null;
  created_at: string;
  updated_at: string;
  customerName?: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  professionalName?: string;
}

interface HistoryRow {
  id: string;
  from_status: string | null;
  to_status: string;
  changed_by_role: string | null;
  changedByName: string | null;
  reason: string | null;
  created_at: string;
}

interface ProfessionalLite {
  id: string;
  name: string;
}

interface ServiceLite {
  id: string;
  name: string;
  status: string;
}

const TONE: Record<string, 'neutral' | 'positive' | 'warning' | 'danger'> = {
  pending: 'warning',
  confirmed: 'positive',
  completed: 'neutral',
  cancelled_by_customer: 'danger',
  cancelled_by_professional: 'danger',
  no_show: 'danger'
};

const ROLE_LABEL: Record<string, string> = {
  owner: 'Proprietário',
  manager: 'Gerente',
  professional: 'Profissional',
  customer: 'Cliente',
  system: 'Sistema'
};

export function PanelAppointmentsPage(): JSX.Element {
  usePageMeta(tr('Agendamentos'), tr('Liste, filtre e gerencie os agendamentos do estabelecimento.'));
  const { active } = useAuth();
  const toast = useToast();
  const businessId = active?.businessId ?? '';
  const canManage = active ? active.role === 'owner' || active.role === 'manager' : false;

  const [statusFilter, setStatusFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [appointments, setAppointments] = useState<ApptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ appointment: ApptRow; history: HistoryRow[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState('');
  const [reschedTime, setReschedTime] = useState('');
  const [reschedReason, setReschedReason] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [professionals, setProfessionals] = useState<ProfessionalLite[]>([]);
  const [services, setServices] = useState<ServiceLite[]>([]);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    try {
      const res = await api.get<{ appointments: ApptRow[] }>(`/api/appointments/${businessId}/appointments?${params.toString()}`);
      setAppointments(res.appointments);
    } catch (err) {
      setError(err);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, statusFilter, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (id: string): Promise<void> => {
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setCancelOpen(false);
    setCancelReason('');
    setRescheduleOpen(false);
    setReschedDate('');
    setReschedTime('');
    setReschedReason('');
    setDetailLoading(true);
    try {
      const res = await api.get<{ appointment: ApptRow; history: HistoryRow[] }>(`/api/appointments/${businessId}/appointments/${id}`);
      setDetail(res);
    } catch (err) {
      setDetailError(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const changeStatus = async (to_ : 'confirmed' | 'completed' | 'no_show' | 'cancelled_by_professional', reason?: string): Promise<void> => {
    if (!detail) return;
    setBusy(`status:${to_}`);
    try {
      await api.patch<{ ok: boolean }>(`/api/appointments/${businessId}/appointments/${detail.appointment.id}/status`, {
        to: to_,
        reason: reason?.trim() || undefined
      });
      toast(tr('Status atualizado para {status}.', { status: statusLabel(to_) }), 'success');
      setCancelOpen(false);
      setCancelReason('');
      const res = await api.get<{ appointment: ApptRow; history: HistoryRow[] }>(
        `/api/appointments/${businessId}/appointments/${detail.appointment.id}`
      );
      setDetail(res);
      await load();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const reschedule = async (): Promise<void> => {
    if (!detail) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(reschedDate) || !/^\d{2}:\d{2}$/.test(reschedTime)) {
      toast(tr('Informe a nova data (AAA-MM-DD) e o horário (HH:MM).'), 'danger');
      return;
    }
    setBusy('reschedule');
    try {
      const res = await api.post<{ ok: boolean; appointment: { scheduled_date: string; start_time: string; code: string } }>(
        `/api/appointments/${businessId}/appointments/${detail.appointment.id}/reschedule`,
        { date: reschedDate, startTime: reschedTime, reason: reschedReason.trim() || undefined }
      );
      toast(
        tr('Remarcado para {date} {time} (novo código {code}). O agendamento original foi cancelado pelo profissional.', {
          date: datePt(res.appointment.scheduled_date),
          time: res.appointment.start_time,
          code: res.appointment.code
        }),
        'success'
      );
      setRescheduleOpen(false);
      setSelectedId(null);
      setDetail(null);
      await load();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const expireHolds = async (): Promise<void> => {
    setBusy('expire');
    try {
      const res = await api.post<{ ok: boolean; expired: number }>(`/api/appointments/${businessId}/expire-holds`);
      toast(tr('{count} reserva(s) pendente(s) expirada(s) por falta de aprovação.', { count: res.expired }), 'success');
      await load();
    } catch (err) {
      toast(errorMessage(err), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const openCreate = async (): Promise<void> => {
    setCreateOpen(true);
    try {
      const [team, svcs] = await Promise.all([
        api.get<{ team: ProfessionalLite[] }>(`/api/team/${businessId}/team`),
        api.get<{ services: ServiceLite[] }>(`/api/services/${businessId}/services`)
      ]);
      setProfessionals(team.team);
      setServices(svcs.services.filter((s) => s.status === 'active'));
    } catch (err) {
      toast(errorMessage(err), 'danger');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Agendamentos")}</h1>
          <p className="nl-hint">{tr("Lista, filtros por status e data, histórico de mudanças e ações do painel.")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage ? (
            <>
              <Button variant="secondary" onClick={() => void openCreate()} icon={<Icon name="plus" size={16} />}>
                {tr("Novo agendamento")}
              </Button>
              <Button variant="ghost" loading={busy === 'expire'} disabled={busy !== null} onClick={() => void expireHolds()}>
                {tr("Expirar reservas sem aprovação")}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Card as="section">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={tr("Status")}>
            {({ id }) => (
              <Select id={id} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">{tr("Abertos (pendentes e confirmados)")}</option>
                <option value="pending">{tr("Pendente")}</option>
                <option value="confirmed">{tr("Confirmado")}</option>
                <option value="completed">{tr("Concluído")}</option>
                <option value="cancelled_by_customer">{tr("Cancelado pelo cliente")}</option>
                <option value="cancelled_by_professional">{tr("Cancelado pelo profissional")}</option>
                <option value="no_show">{tr("Não compareceu")}</option>
              </Select>
            )}
          </Field>
          <Field label={tr("De")} hint="Data inicial (AAA-MM-DD)">
            {({ id }) => <Input id={id} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}
          </Field>
          <Field label={tr("Até")} hint="Data final (AAA-MM-DD)">
            {({ id }) => <Input id={id} type="date" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
        </div>
        <p className="nl-hint mt-2">
          {tr("Sem filtro de status, a API retorna apenas pendentes e confirmados. A lista é limitada a 500 registros por consulta.")}
        </p>
      </Card>

      {loading ? (
        <Card>
          <Spinner label={tr("Carregando agendamentos…")} />
        </Card>
      ) : null}

      {!loading && error ? <ListError error={error} /> : null}

      {!loading && !error ? (
        <Card as="section" className="overflow-x-auto">
          {appointments.length === 0 ? (
            <EmptyState
              title={tr("Nenhum agendamento encontrado")}
              description={tr("Ajuste os filtros de status e de período ou registre um novo atendimento.")}
            />
          ) : (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-line text-muted">
                  <th className="py-2 pr-3 font-semibold">{tr("Data / hora")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Serviço")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Cliente")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Profissional")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Valor")}</th>
                  <th className="py-2 pr-3 font-semibold">{tr("Status")}</th>
                  <th className="py-2 font-semibold">{tr("Código")}</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => (
                  <tr
                    key={a.id}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-surface"
                    onClick={() => void openDetail(a.id)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void openDetail(a.id);
                    }}
                  >
                    <td className="py-2.5 pr-3">
                      {datePt(a.scheduled_date)} {a.start_time}–{a.end_time}
                    </td>
                    <td className="py-2.5 pr-3">{a.service_name}</td>
                    <td className="py-2.5 pr-3">{a.customerName}</td>
                    <td className="py-2.5 pr-3">{a.professionalName}</td>
                    <td className="py-2.5 pr-3">{brl(a.price_cents)}</td>
                    <td className="py-2.5 pr-3">
                      <StatusPill tone={TONE[a.status] ?? 'neutral'}>{statusLabel(a.status)}</StatusPill>
                    </td>
                    <td className="py-2.5 text-muted">{a.code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : null}

      <Modal open={selectedId !== null} onClose={() => setSelectedId(null)} title={tr("Detalhe do agendamento")}>
        {detailLoading ? <Spinner label={tr("Carregando detalhe…")} /> : null}
        {!detailLoading && detailError ? <ListError error={detailError} /> : null}
        {detail && !detailLoading ? (
          <div className="space-y-4">
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p>
                <span className="font-semibold">{tr("Serviço:")}</span> {detail.appointment.service_name} ({detail.appointment.service_duration_minutes} {tr("min)")}
              </p>
              <p>
                <span className="font-semibold">{tr("Valor:")}</span> {brl(detail.appointment.price_cents)}
              </p>
              <p>
                <span className="font-semibold">{tr("Data:")}</span> {datePt(detail.appointment.scheduled_date)} {detail.appointment.start_time}–{detail.appointment.end_time}
              </p>
              <p>
                <span className="font-semibold">{tr("Status:")}</span> <StatusPill tone={TONE[detail.appointment.status] ?? 'neutral'}>{statusLabel(detail.appointment.status)}</StatusPill>
              </p>
              <p>
                <span className="font-semibold">{tr("Cliente:")}</span> {detail.appointment.customerName}
                {detail.appointment.customerPhone ? ` · ${detail.appointment.customerPhone}` : ''}
                {detail.appointment.customerEmail ? ` · ${detail.appointment.customerEmail}` : ''}
              </p>
              <p>
                <span className="font-semibold">{tr("Profissional:")}</span> {detail.appointment.professionalName}
              </p>
              {detail.appointment.notes ? (
                <p className="sm:col-span-2">
                  <span className="font-semibold">{tr("Observações:")}</span> {detail.appointment.notes}
                </p>
              ) : null}
              {detail.appointment.cancellation_reason ? (
                <p className="sm:col-span-2">
                  <span className="font-semibold">{tr("Motivo do cancelamento:")}</span> {detail.appointment.cancellation_reason}
                </p>
              ) : null}
              {detail.appointment.hold_expires_at ? (
                <p className="sm:col-span-2 nl-hint">
                  {tr("Reserva expira em")} {dateTimePt(detail.appointment.hold_expires_at)} {tr("se não for aprovada.")}
                </p>
              ) : null}
            </div>

            <div>
              <h3 className="mb-2 font-semibold">{tr("Histórico de mudanças")}</h3>
              {detail.history.length === 0 ? (
                <p className="nl-hint">{tr("Nenhuma mudança registrada.")}</p>
              ) : (
                <ul className="space-y-2">
                  {detail.history.map((h) => (
                    <li key={h.id} className="rounded-2xl border border-line bg-surface px-3 py-2 text-sm">
                      <p className="font-semibold">
                        {fromStatusText(h.from_status)} → {statusLabel(h.to_status)}
                      </p>
                      <p className="nl-hint">
                        {dateTimePt(h.created_at)} · {h.changedByName ?? 'Desconhecido'}
                        {h.changed_by_role ? ` (${roleLabel(h.changed_by_role)})` : ''}
                        {h.reason ? ` — ${h.reason}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {detail.appointment.status === 'pending' ? (
                <Button loading={busy === 'status:confirmed'} disabled={busy !== null} onClick={() => void changeStatus('confirmed')}>
                  {tr("Confirmar")}
                </Button>
              ) : null}
              {detail.appointment.status === 'confirmed' ? (
                <Button loading={busy === 'status:completed'} disabled={busy !== null} onClick={() => void changeStatus('completed')}>
                  {tr("Concluir")}
                </Button>
              ) : null}
              {detail.appointment.status === 'confirmed' ? (
                <Button variant="secondary" loading={busy === 'status:no_show'} disabled={busy !== null} onClick={() => void changeStatus('no_show')}>
                  {tr("Não compareceu")}
                </Button>
              ) : null}
              {['pending', 'confirmed'].includes(detail.appointment.status) ? (
                <>
                  <Button variant="danger" disabled={busy !== null} onClick={() => setCancelOpen((v) => !v)}>
                    {tr("Cancelar")}
                  </Button>
                  <Button variant="secondary" disabled={busy !== null} onClick={() => setRescheduleOpen((v) => !v)}>
                    {tr("Remarcar")}
                  </Button>
                </>
              ) : null}
            </div>

            {cancelOpen ? (
              <div className="space-y-2 rounded-2xl border border-danger bg-card p-3">
                <Field label={tr("Motivo do cancelamento")} required hint="Obrigatório — o backend recusa cancelamento sem motivo.">
                  {({ id }) => (
                    <Textarea id={id} rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={tr("Ex.: imprevisto com a agenda do profissional")} />
                  )}
                </Field>
                <Button
                  variant="danger"
                  loading={busy === 'status:cancelled_by_professional'}
                  disabled={busy !== null || cancelReason.trim().length === 0}
                  onClick={() => void changeStatus('cancelled_by_professional', cancelReason)}
                >
                  {tr("Confirmar cancelamento")}
                </Button>
              </div>
            ) : null}

            {rescheduleOpen ? (
              <div className="space-y-2 rounded-2xl border border-line bg-surface p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label={tr("Nova data")} required>
                    {({ id }) => <Input id={id} type="date" min={todayIso()} value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} />}
                  </Field>
                  <Field label={tr("Novo horário")} required>
                    {({ id }) => <Input id={id} type="time" value={reschedTime} onChange={(e) => setReschedTime(e.target.value)} />}
                  </Field>
                </div>
                <Field label={tr("Motivo (opcional)")}>
                  {({ id }) => <Input id={id} value={reschedReason} onChange={(e) => setReschedReason(e.target.value)} maxLength={300} />}
                </Field>
                <p className="nl-hint text-xs">
                  {tr("A remarcação cria um novo agendamento validado pela agenda do profissional; o original é cancelado pelo profissional com o motivo informado.")}
                </p>
                <Button loading={busy === 'reschedule'} disabled={busy !== null} onClick={() => void reschedule()}>
                  {tr("Remarcar")}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      {createOpen ? (
        <CreateAppointmentModal
          businessId={businessId}
          professionals={professionals}
          services={services}
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function roleLabel(role: string): string {
  const label = ROLE_LABEL[role];
  return label ? tr(label) : role;
}

function fromStatusText(from: string | null): string {
  if (!from) return tr('Criado');
  if (from.startsWith('rescheduled_from:')) return tr('Remarcado de outro agendamento');
  return statusLabel(from);
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 402) {
    return tr('{message} Contrate um plano em /painel/plano.', { message: err.message });
  }
  if (err instanceof ApiError && err.status === 403) {
    return tr('Permissão insuficiente para esta ação. {message}', { message: err.message });
  }
  return err instanceof Error ? err.message : tr('Não foi possível concluir a operação.');
}

function ListError({ error }: { error: unknown }): JSX.Element {
  if (error instanceof ApiError && error.status === 402) {
    return (
      <Alert tone="danger" title={tr("Assinatura necessária")}>
        {error.message}{' '}
        <Link className="font-semibold underline" to="/painel/plano">
          {tr("Ir para Meu plano")}
        </Link>
        .
      </Alert>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Alert tone="warning" title={tr("Permissão insuficiente")}>
        {tr("Seu perfil não tem acesso aos agendamentos deste estabelecimento.")} {error.message}
      </Alert>
    );
  }
  return <Alert tone="danger" title={tr("Erro ao carregar")}>{error instanceof Error ? error.message : 'Tente novamente.'}</Alert>;
}

interface CreateFormState {
  professionalId: string;
  serviceId: string;
  date: string;
  startTime: string;
  notes: string;
  name: string;
  phone: string;
  email: string;
}

function CreateAppointmentModal({
  businessId,
  professionals,
  services,
  onClose,
  onCreated
}: {
  businessId: string;
  professionals: ProfessionalLite[];
  services: ServiceLite[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}): JSX.Element {
  const toast = useToast();
  const [form, setForm] = useState<CreateFormState>({
    professionalId: professionals[0]?.id ?? '',
    serviceId: services[0]?.id ?? '',
    date: todayIso(),
    startTime: '',
    notes: '',
    name: '',
    phone: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const set = (patch: Partial<CreateFormState>): void => setForm((f) => ({ ...f, ...patch }));

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/appointments/${businessId}/appointments`, {
        professionalId: form.professionalId,
        serviceId: form.serviceId,
        date: form.date,
        startTime: form.startTime,
        notes: form.notes.trim() || undefined,
        customer: {
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined
        }
      });
      toast('Agendamento criado pelo painel.', 'success');
      await onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const valid =
    form.professionalId !== '' &&
    form.serviceId !== '' &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.date) &&
    /^\d{2}:\d{2}$/.test(form.startTime) &&
    form.name.trim().length >= 2;

  return (
    <Modal open onClose={onClose} title={tr("Novo agendamento")}>
      <div className="space-y-3">
        {professionals.length === 0 || services.length === 0 ? (
          <Alert tone="warning" title={tr("Cadastre serviços")}>
            {tr("É preciso ter ao menos um profissional e um serviço ativo para criar agendamentos pelo painel.")}
          </Alert>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={tr("Profissional")} required>
            {({ id }) => (
              <Select id={id} value={form.professionalId} onChange={(e) => set({ professionalId: e.target.value })}>
                {professionals.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={tr("Serviço")} required>
            {({ id }) => (
              <Select id={id} value={form.serviceId} onChange={(e) => set({ serviceId: e.target.value })}>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={tr("Data")} required>
            {({ id }) => <Input id={id} type="date" min={todayIso()} value={form.date} onChange={(e) => set({ date: e.target.value })} />}
          </Field>
          <Field label={tr("Horário de início")} required>
            {({ id }) => <Input id={id} type="time" value={form.startTime} onChange={(e) => set({ startTime: e.target.value })} />}
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={tr("Nome do cliente")} required>
            {({ id }) => <Input id={id} value={form.name} onChange={(e) => set({ name: e.target.value })} minLength={2} maxLength={120} />}
          </Field>
          <Field label={tr("Telefone")}>
            {({ id }) => <Input id={id} value={form.phone} onChange={(e) => set({ phone: e.target.value })} maxLength={24} />}
          </Field>
          <Field label={tr("E-mail")}>
            {({ id }) => <Input id={id} type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />}
          </Field>
        </div>
        <Field label={tr("Observações")} hint="Máximo 500 caracteres.">
          {({ id }) => <Textarea id={id} rows={2} maxLength={500} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />}
        </Field>
        {error ? <ListError error={error} /> : null}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          {tr("Cancelar")}
        </Button>
        <Button onClick={() => void submit()} loading={saving} disabled={!valid || saving}>
          {tr("Criar agendamento")}
        </Button>
      </div>
    </Modal>
  );
}
